import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Panel, PageHeader, StatusBadge, Chip, PrimaryButton, GhostButton, StyledSelect, TextField, Label } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, ShieldCheck, Loader2, Users as UsersIcon, KeyRound } from "lucide-react";

function RoleBadge({ role, roles }) {
  const label = roles.find((r) => r.name === role)?.label || role;
  return <Chip tone="orange">{label}</Chip>;
}

export default function Users() {
  const { user: me, hasPerm } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "warehouse_staff" });
  const [tempCred, setTempCred] = useState(null);

  const canWrite = hasPerm("users:create");

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/users/roles")]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/users", form);
      setInviteOpen(false);
      setForm({ name: "", email: "", role: "warehouse_staff" });
      setTempCred({ email: data.email, password: data.temp_password });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Role updated");
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleStatus = async (u) => {
    const status = u.status === "active" ? "inactive" : "active";
    try {
      const { data } = await api.patch(`/users/${u.id}`, { status });
      toast.success(`Account ${status === "active" ? "activated" : "deactivated"}`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, status: data.status } : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const renameUser = async (u) => {
    const name = window.prompt("New name", u.name);
    if (!name || name === u.name) return;
    try {
      const { data } = await api.patch(`/users/${u.id}`, { name });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, name: data.name } : x)));
      toast.success("Name updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHeader
        title="Users & Roles"
        subtitle="Manage accounts, assign roles and review the permission matrix."
        actions={canWrite && (
          <PrimaryButton onClick={() => setInviteOpen(true)} data-testid="invite-user-button">
            <UserPlus className="h-4 w-4" /> Invite User
          </PrimaryButton>
        )}
      />

      <div className="flex gap-2 border-b border-slate-800">
        {[["users", "Users", UsersIcon], ["roles", "Roles & Permissions", KeyRound]].map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? "border-orange-500 text-orange-400" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
      ) : tab === "users" ? (
        <Panel className="overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium text-slate-200">{u.name}</td>
                  <td className="px-4 py-3 text-slate-400">{u.email}</td>
                  <td className="px-4 py-3">
                    {canWrite && u.email !== me?.email ? (
                      <StyledSelect value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} data-testid={`role-select-${u.email}`} className="max-w-[220px]">
                        {roles.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
                      </StyledSelect>
                    ) : (
                      <RoleBadge role={u.role} roles={roles} />
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={u.status || "active"} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canWrite && u.email !== me?.email ? (
                      <>
                        <GhostButton className="mr-1.5" onClick={() => renameUser(u)} data-testid={`edit-user-${u.email}`}>Edit</GhostButton>
                        <GhostButton className="mr-1.5" onClick={() => toggleStatus(u)} data-testid={`toggle-status-${u.email}`}>
                          {u.status === "active" ? "Deactivate" : "Activate"}
                        </GhostButton>
                        {hasPerm("users:delete") && <button onClick={() => removeUser(u)} className="text-slate-500 hover:text-rose-400 text-xs" data-testid={`delete-user-${u.email}`}>delete</button>}
                      </>
                    ) : <span className="text-xs text-slate-600 font-mono">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="roles-grid">
          {roles.map((r) => (
            <Panel key={r.name} className="p-5">
              <div className="flex items-center justify-between">
                <div className="font-heading text-lg font-bold uppercase tracking-wide text-slate-100">{r.label}</div>
                <Chip>{r.permission_keys.includes("*") ? "ALL" : `${r.permission_keys.length} perms`}</Chip>
              </div>
              <p className="text-sm text-slate-400 mt-1">{r.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {r.permission_keys.includes("*")
                  ? <Chip tone="emerald">system:* (full access)</Chip>
                  : r.permission_keys.map((p) => <Chip key={p}>{p}</Chip>)}
              </div>
            </Panel>
          ))}
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-orange-500" /> Invite New User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={invite} className="space-y-4">
            <div><Label>Full name</Label><div className="mt-1.5"><TextField required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div></div>
            <div><Label>Email</Label><div className="mt-1.5"><TextField type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div></div>
            <p className="text-xs text-slate-500">A secure temporary password will be generated and shown once after creation. The user must change it on first login.</p>
            <div><Label>Role</Label><div className="mt-1.5"><StyledSelect value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role">
              {roles.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
            </StyledSelect></div></div>
            <DialogFooter>
              <GhostButton type="button" onClick={() => setInviteOpen(false)}>Cancel</GhostButton>
              <PrimaryButton type="submit" disabled={saving} data-testid="invite-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Create User
              </PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempCred} onOpenChange={(o) => !o && setTempCred(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="temp-cred-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2"><KeyRound className="h-5 w-5 text-orange-500" /> User Created</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Share this one-time temporary password with <span className="text-slate-200">{tempCred?.email}</span>. They will be asked to change it on first login.</p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 font-mono text-orange-300 text-sm break-all" data-testid="temp-password-value">{tempCred?.password}</code>
            <GhostButton type="button" onClick={() => { navigator.clipboard?.writeText(tempCred?.password || ""); toast.success("Copied"); }} data-testid="copy-temp-password">Copy</GhostButton>
          </div>
          <DialogFooter><PrimaryButton type="button" onClick={() => setTempCred(null)}>Done</PrimaryButton></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
